import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { BookOpen, ClipboardList, FileText, Building2, Files, BarChart3, TrendingUp, CheckCircle2 } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useAuth } from "../hooks/useAuth";
import { API_BASE_URL } from "@/utils/env";
import Sidebar from "../components/Sidebar";
import MobileNav from "../components/MobileNav";
import UserProfileMenu from "../components/UserProfileMenu";

const SVC_NAMES = [
    'Bookkeeping',
    'VAT Registration',
    'VAT Filing',
    'Corporate Tax Registration',
    'Corporate Tax Filing',
];

const SVC_ICONS: LucideIcon[] = [BookOpen, ClipboardList, FileText, Building2, Files];
const SVC_COLORS = [
    { bg:'#EFF6FF', border:'#BFDBFE', text:'#1D4ED8' },
    { bg:'#F0FDF4', border:'#BBF7D0', text:'#166534' },
    { bg:'#F0FDF4', border:'#BBF7D0', text:'#166534' },
    { bg:'#FFF7ED', border:'#FED7AA', text:'#C2410C' },
    { bg:'#FFF7ED', border:'#FED7AA', text:'#C2410C' },
];

type PurchasedPkg = {
    name: string;
    flags: number[];
    band: string;
    purchasedAt: string;
};

function PkgCard({ pkg, index }: { pkg: PurchasedPkg; index: number }) {
    const included = SVC_NAMES.filter((_, i) => pkg.flags[i] === 1);
    const date = pkg.purchasedAt
        ? new Date(pkg.purchasedAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
        : '—';

    return (
        <div style={{
            background: '#fff',
            border: '1px solid #E7EAEE',
            borderRadius: 20,
            overflow: 'hidden',
            boxShadow: '0 2px 12px rgba(23,38,58,.06)',
            animation: `fadeUp 0.35s ${index * 0.07}s both`,
        }}>
            {/* Colored top accent */}
            <div style={{ height: 4, background: 'linear-gradient(90deg,#FFD54F,#E6B400)' }} />

            <div style={{ padding: '24px 28px' }}>
                {/* Header row */}
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 20 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                        <div style={{ width: 48, height: 48, borderRadius: 14, background: '#FFFBEB', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <BarChart3 className="w-6 h-6" style={{ color: '#E6B400' }} />
                        </div>
                        <div>
                            <h2 style={{ fontSize: 17, fontWeight: 800, color: '#17263A', margin: 0, lineHeight: 1.3 }}>{pkg.name}</h2>
                            <p style={{ color: '#6E7888', fontSize: 12.5, fontWeight: 600, margin: '3px 0 0' }}>
                                Active since {date}
                            </p>
                        </div>
                    </div>
                    <span style={{ background: '#D1FAE5', color: '#065F46', fontSize: 11.5, fontWeight: 700, padding: '5px 12px', borderRadius: 999, whiteSpace: 'nowrap', flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                        <CheckCircle2 className="w-3.5 h-3.5" /> Active
                    </span>
                </div>

                {/* Included services */}
                <div style={{ marginBottom: 18 }}>
                    <p style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '.07em', color: '#6E7888', fontWeight: 700, margin: '0 0 12px' }}>
                        Included services
                    </p>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                        {included.map((name, i) => {
                            const idx = SVC_NAMES.indexOf(name);
                            const col = SVC_COLORS[idx] || SVC_COLORS[0];
                            const SvcIcon: LucideIcon = SVC_ICONS[idx] ?? BookOpen;
                            return (
                                <div key={name} style={{ display: 'flex', alignItems: 'center', gap: 6, background: col.bg, border: `1px solid ${col.border}`, borderRadius: 8, padding: '6px 12px', fontSize: 12.5, fontWeight: 700, color: col.text }}>
                                    <SvcIcon className="w-3.5 h-3.5" />
                                    {name}
                                </div>
                            );
                        })}
                        {pkg.flags[0] === 1 && pkg.band && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 8, padding: '6px 12px', fontSize: 12.5, fontWeight: 700, color: '#92400E' }}>
                                <TrendingUp className="w-3.5 h-3.5" /> {pkg.band} txns/mo
                            </div>
                        )}
                    </div>
                </div>

                {/* Stats row */}
                <div style={{ display: 'flex', gap: 0, background: '#F8F9FB', borderRadius: 12, overflow: 'hidden' }}>
                    {[
                        { label: 'Status', value: 'Active', valueColor: '#15824E' },
                        { label: 'Services', value: String(included.length) },
                        { label: 'Since', value: pkg.purchasedAt ? new Date(pkg.purchasedAt).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : '—' },
                    ].map((stat, i) => (
                        <div key={stat.label} style={{ flex: 1, padding: '14px 16px', borderLeft: i > 0 ? '1px solid #E7EAEE' : 'none' }}>
                            <div style={{ fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '.06em', color: '#6E7888', fontWeight: 700, marginBottom: 3 }}>{stat.label}</div>
                            <div style={{ fontSize: 14, fontWeight: 800, color: stat.valueColor || '#17263A' }}>{stat.value}</div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

export default function MyActiveServices() {
    const navigate = useNavigate();
    const { user, logout } = useAuth();
    const [pkgs, setPkgs] = useState<PurchasedPkg[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!user) return;
        const tokensStr = localStorage.getItem('authTokens');
        const idToken = tokensStr ? JSON.parse(tokensStr).idToken : null;
        fetch(`${API_BASE_URL}/auth/user/${user.uid}`, {
            headers: idToken ? { Authorization: `Bearer ${idToken}` } : {},
        })
            .then(r => r.json())
            .then(json => {
                if (json.success && json.user) {
                    const raw = json.user.purchasedPackages;
                    if (raw && typeof raw === 'object') {
                        // RTDB returns push-object {-Mxyz: {...}, ...} or plain array
                        const arr: PurchasedPkg[] = (Array.isArray(raw) ? raw : Object.values(raw))
                            .filter((p: any) => p && p.name) as PurchasedPkg[];
                        // Sort newest first
                        arr.sort((a, b) => new Date(b.purchasedAt).getTime() - new Date(a.purchasedAt).getTime());
                        setPkgs(arr);
                    } else if (json.user.purchasedPackage?.name) {
                        setPkgs([json.user.purchasedPackage]);
                    }
                }
            })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [user]);

    const handleLogout = async () => { await logout(); navigate('/'); };

    return (
        <div style={{ display: 'flex', minHeight: '100vh', background: '#F5F6F8', fontFamily: "'Montserrat',system-ui,sans-serif" }}>
            <style>{`
                @keyframes fadeUp {
                    from { opacity:0; transform:translateY(14px); }
                    to   { opacity:1; transform:translateY(0); }
                }
            `}</style>
            <Sidebar onLogout={handleLogout} />
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }} className="md:ms-64">
                <MobileNav onLogout={handleLogout} />

                {/* Header */}
                <header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 32px', borderBottom: '1px solid #E7EAEE', background: '#fff' }}>
                    <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                            <button onClick={() => navigate('/dashboard')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6E7888', fontSize: 13, fontWeight: 600, fontFamily: 'inherit', padding: 0 }}>
                                Dashboard
                            </button>
                            <span style={{ color: '#C4CAD1', fontSize: 13 }}>/</span>
                            <span style={{ color: '#17263A', fontSize: 13, fontWeight: 600 }}>My Active Services</span>
                        </div>
                        <h1 style={{ fontSize: 24, fontWeight: 800, color: '#17263A', margin: 0 }}>My Active Services</h1>
                        <p style={{ color: '#6E7888', fontSize: 13.5, fontWeight: 500, marginTop: 2 }}>
                            {pkgs.length > 0 ? `${pkgs.length} active package${pkgs.length > 1 ? 's' : ''}` : 'Track and manage your accounting & compliance services'}
                        </p>
                    </div>
                    <UserProfileMenu />
                </header>

                {/* Content */}
                <div style={{ padding: '32px', maxWidth: 860, width: '100%', margin: '0 auto' }}>
                    {loading ? (
                        <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 100 }}>
                            <div className="w-10 h-10 border-4 border-amber-400 border-t-transparent rounded-full animate-spin" />
                        </div>
                    ) : pkgs.length > 0 ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                            {pkgs.map((pkg, i) => (
                                <PkgCard key={`${pkg.purchasedAt}-${i}`} pkg={pkg} index={i} />
                            ))}

                            {/* Add service CTA */}
                            <button
                                onClick={() => navigate('/services')}
                                style={{ width: '100%', background: '#fff', border: '1.5px dashed #E7EAEE', borderRadius: 18, padding: '18px 24px', display: 'flex', alignItems: 'center', gap: 14, cursor: 'pointer', fontFamily: 'inherit', transition: 'border-color .15s, background .15s', textAlign: 'left', marginTop: 4 }}
                                onMouseEnter={e => { e.currentTarget.style.borderColor = '#E6B400'; e.currentTarget.style.background = '#FFFBEB'; }}
                                onMouseLeave={e => { e.currentTarget.style.borderColor = '#E7EAEE'; e.currentTarget.style.background = '#fff'; }}
                            >
                                <div style={{ width: 44, height: 44, borderRadius: 12, background: '#F3F4F6', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, fontWeight: 800, color: '#6B7280', flexShrink: 0 }}>
                                    +
                                </div>
                                <div>
                                    <p style={{ fontSize: 15, fontWeight: 800, color: '#17263A', margin: 0 }}>Add or upgrade a service</p>
                                    <p style={{ fontSize: 13, color: '#6E7888', fontWeight: 600, margin: '2px 0 0' }}>Explore payroll, audit, VAT, and CFO services</p>
                                </div>
                                <svg viewBox="0 0 24 24" fill="none" stroke="#C4CAD1" strokeWidth="2" style={{ width: 18, height: 18, marginLeft: 'auto', flexShrink: 0 }}>
                                    <path d="M9 18l6-6-6-6" />
                                </svg>
                            </button>
                        </div>
                    ) : (
                        /* Empty state */
                        <div style={{ background: '#fff', border: '1px solid #E7EAEE', borderRadius: 24, padding: '64px 32px', textAlign: 'center', boxShadow: '0 2px 12px rgba(23,38,58,.06)' }}>
                            <div style={{ width: 72, height: 72, borderRadius: '50%', background: '#F3F4F6', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
                                <ClipboardList className="w-9 h-9" style={{ color: '#9CA3AF' }} />
                            </div>
                            <h2 style={{ fontSize: 22, fontWeight: 800, color: '#17263A', marginBottom: 8 }}>No active services yet</h2>
                            <p style={{ color: '#6E7888', fontSize: 15, marginBottom: 36, maxWidth: 380, margin: '0 auto 36px' }}>
                                Choose a package to get started with your accounting & compliance services.
                            </p>
                            <button
                                onClick={() => navigate('/services')}
                                style={{ background: '#FFD54F', color: '#17263A', border: 'none', borderRadius: 12, padding: '14px 40px', fontSize: 15, fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit', boxShadow: '0 4px 16px rgba(230,180,0,.3)' }}
                            >
                                Browse services →
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
